import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PageWrapper from "../pageWrapper";

//CSS
import "./messageViewPage.css";
import "../0PageStyles.css";

//Components
import Title from "../../Components/Other Components/Title";
import Button from "../../Components/Other Components/button";
import InputField from "../../Components/Other Components/inputField";
import PopupConfirmation from "../../Components/Other Components/popupConfirmation";
import PageLoader from "../../Components/Other Components/pageLoader";

//Functions and hooks
import { MessageTypes } from "../../BackendAccess/FrontendHelpers";
import {
  DeleteMessages,
  CheckForMessage,
} from "../../BackendAccess/MessageHooks";
import { AcceptEmployeeRequest } from "../../BackendAccess/EmployeeHooks";
import { useNotification } from "../../Contexts/NotificationContext";
import { useErrorHandler } from "../../hooks/errorHandler";

export default function MessageViewPage() {
  const constMessage = {
    MessageID: -1,
    Header: "Header",
    Body: "Body Lorem ipsum dolor sit amet consectetur adipisicing elit. Consequuntur sed dicta maxime sunt, quam distinctio vel animi ad nisi! Odit voluptatibus ratione eum reprehenderit eveniet quaerat quam minima cumque. Atque deleniti nisi, sapiente optio maiores dolore alias delectus, sequi dolorum ab pariatur, libero magni error nobis repudiandae. Nesciunt, porro distinctio",
    SenderName: "Some Account",
    DateTime: "13.02.2005/20:40",
    Type: MessageTypes.ANNOUNCEMENT,
    Token: "",
  };
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pageError, setPageError] = useState(null);

  const { showNotification } = useNotification();
  useErrorHandler(error, showNotification, setPageError);

  const { t } = useTranslation();

  const { message: StateMessage } = location.state;

  const LocalMessage = StateMessage ? StateMessage : constMessage;

  const [showPopup, setShowPopup] = useState(false);
  const [deleteBtnDisabled, setDeleteBtnDisabled] = useState(false);

  const checkedType = Object.values(MessageTypes).includes(LocalMessage.Type)
    ? LocalMessage.Type
    : MessageTypes.ANNOUNCEMENT;

  const handleCancelClick = () => {
    navigate(-1);
  };

  const clickOnDelete = () => {
    setShowPopup(true);
  };

  const handleConfirmDelete = async () => {
    setDeleteBtnDisabled(true);
    showNotification("Deleting message...", "info");
    try {
      const [deleteData, deleteError] = await DeleteMessages([
        LocalMessage.MessageID,
      ]);
      if (deleteError) {
        showNotification(deleteError, "error");
        return;
      }
      showNotification("Message deleted successfully!", "success");
      navigate(-1);
    } catch (err) {
      showNotification("Server error.", "error");
    } finally {
      setDeleteBtnDisabled(false);
    }
  };

  const handleCancelDelete = () => {
    setShowPopup(false);
  };

  const handleAcceptClick = async () => {
    try {
      const [data, error] = await AcceptEmployeeRequest(LocalMessage.MessageID);
      if (error) {
        setError(error);
      } else {
        showNotification("Invite accepted!", "success");
        navigate(-1);
      }
    } catch (err) {
      setError({ err: "Server error.", errCode: 500 });
    }
  };
  const checkIfMessageExists = async () => {
    try {
      const [bMessageExists, error] = await CheckForMessage(
        LocalMessage.MessageID
      );
      if (error) {
        setError(error);
        navigate(-1);
      } else {
      }
    } catch (err) {
      setError({ err: "Server error.", errCode: 500 });
    }
  };

  useEffect(() => {
    checkIfMessageExists();
  }, []);

  const tutorialSteps = [
    {
      target: null,
      title: t("Viewing a message", { ns: "tutorial" }),
      message: t(
        "This page shows one message's full text along with who sent it and when.",
        { ns: "tutorial" }
      ),
    },
    ...(checkedType === MessageTypes.REQUEST
      ? [
          {
            target: "message-view-accept-btn",
            title: t("Accept", { ns: "tutorial" }),
            message: t(
              "This message is a join request from someone wanting to become your employee. Click here to accept it and add them to your company.",
              { ns: "tutorial" }
            ),
          },
        ]
      : []),
    {
      target: "message-view-delete-btn",
      title: t("Delete", { ns: "tutorial" }),
      message: t(
        "Removes this message from your inbox. This cannot be undone.",
        { ns: "tutorial" }
      ),
    },
  ];

  return (
    <PageWrapper tutorialId="messageView" tutorialSteps={tutorialSteps}>
      <PageLoader loading={loading} error={pageError}>
        <div className="page-content">
          <Title className="mt-3">{LocalMessage.Header}</Title>
          <div className="message-view-body-container">
            <InputField
              readOnly={true}
              largeField={true}
              value={LocalMessage.Body}
            />
          </div>
          <div className="message-view-details-section">
            <span className="message-view-sender">
              Sender:{" "}
              <span className="textcolor-span">{LocalMessage.SenderName}</span>
            </span>
            <span className="message-view-datetime">
              Date and time:{" "}
              <span className="textcolor-span">{LocalMessage.DateTime}</span>
            </span>
          </div>
          <div className="btn-container">
            {checkedType === MessageTypes.REQUEST ? (
              <Button
                btnStyle={"btn-positive"}
                icon="apply"
                onClick={handleAcceptClick}
                className="mb-2 mt-2"
                tutorialId="message-view-accept-btn"
              >
                Accept
              </Button>
            ) : null}
            <Button
              btnStyle={"btn-negative"}
              icon="delete"
              onClick={clickOnDelete}
              disabled={deleteBtnDisabled}
              className={
                "mb-2" + (checkedType === MessageTypes.REQUEST ? "" : " mt-2")
              }
              tutorialId="message-view-delete-btn"
            >
              Delete
            </Button>
            <Button onClick={handleCancelClick} className="mb-2">
              Cancel
            </Button>
          </div>
        </div>
        {showPopup && (
          <PopupConfirmation
            style={"warning"}
            message={
              <p>
                Are you sure you want to delete this message?
                <br /> This action will delete this message from your inbox!
                <br />
                <span className="text-negative">
                  This action cannot be undone.
                </span>
              </p>
            }
            onConfirm={handleConfirmDelete}
            onCancel={handleCancelDelete}
          />
        )}
      </PageLoader>
    </PageWrapper>
  );
}
